/** User creation form with responsive field groups for staff and student accounts. */

import {
  Badge,
  Box,
  HStack,
  Select,
  SimpleGrid,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { FormField, FormPageLayout, Input } from '@grcs/components';
import {
  useUserOperations,
  useClassOperations,
  useSemesterOperations,
} from '@grcs/hook/useDatabase';
import { UserProfile, UserRole } from '@grcs/types/UserRole';
import { ClassProfile } from '@grcs/types/DataTypes';
import { validateDob } from '@grcs/utils/validation/validateDob';
import { validateEmail } from '@grcs/utils/validation/validateEmail';
import {
  addNewStudent,
  generateStudentUsername,
} from '@grcs/utils/users/addNewStudent';
import { useData } from '@grcs/context/DataContext';
import { validateStudentInput } from '@grcs/utils/users/studentInput';
import { AccountService } from '@grcs/utils/auth/AccountService';
import { staffPasswordError } from '@grcs/utils/auth/staffPasswordPolicy';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

interface UserFormData {
  first_name: string;
  last_name: string;
  email: string;
  dob: string;
  role: UserRole;
  password: string;
  confirmPassword: string;
  class_token?: string;
  class_name?: string;
}

export const AdminUserAddPageEnhanced: React.FC = () => {
  const navigate = useNavigate();
  const { roleType } = useParams();
  const { supabase } = useData();
  const userOps = useUserOperations();
  const classOps = useClassOperations();
  const semesterOps = useSemesterOperations();
  const toast = useToast();

  const [formData, setFormData] = useState<UserFormData>({
    first_name: '',
    last_name: '',
    email: '',
    dob: '',
    role: (roleType as UserRole) || UserRole.STUDENT,
    password: '',
    confirmPassword: '',
    class_token: '',
    class_name: '',
  });

  // Preserve the request across retries; edits start a new creation attempt.
  const creationRequestId = useRef<string>();
  const [errors, setErrors] = useState<Partial<UserFormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableClasses, setAvailableClasses] = useState<ClassProfile[]>([]);
  const [currentSemester, setCurrentSemester] = useState<string>('');
  const [isLoadingClasses, setIsLoadingClasses] = useState(false);

  // Load current semester and available classes for students
  useEffect(() => {
    const loadClassData = async () => {
      if ((roleType as UserRole) !== UserRole.STUDENT) return;

      try {
        setIsLoadingClasses(true);

        // Get current semester
        const { data: semesterData, error: semesterError } =
          await semesterOps.getCurrentSemesterYear();
        if (semesterError) {
          console.error('Error fetching current semester:', semesterError);
          return;
        }

        if (semesterData && semesterData.length > 0) {
          const semester = semesterData[0].semester_year;
          setCurrentSemester(semester);

          // Get available classes for current semester
          const { data: classesData, error: classesError } =
            await classOps.getClassesBySemester(semester);
          if (classesError) {
            console.error('Error fetching classes:', classesError);
            toast({
              title: 'Error Loading Classes',
              description: 'Could not load available classes for assignment.',
              status: 'warning',
              duration: 4000,
              isClosable: true,
            });
            return;
          }

          setAvailableClasses(classesData || []);
        } else {
          toast({
            title: 'No Active Semester',
            description:
              'No active semester found. Class assignment will not be available.',
            status: 'info',
            duration: 4000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error('Error loading class data:', error);
        toast({
          title: 'Error Loading Data',
          description: 'Failed to load class information.',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      } finally {
        setIsLoadingClasses(false);
      }
    };

    loadClassData();
  }, [roleType, semesterOps, classOps, toast]);

  const displayRole = () => {
    switch ((roleType as UserRole) || UserRole.STUDENT) {
      case UserRole.STUDENT:
        return 'Student';
      case UserRole.TEACHER:
        return 'Teacher';
      case UserRole.ADMIN:
        return 'Admin';
      default:
        return 'User';
    }
  };

  const handleInputChange = (field: keyof UserFormData, value: string) => {
    creationRequestId.current = undefined;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Handle class selection - also set class_name
    if (field === 'class_token' && value) {
      const selectedClass = availableClasses.find(
        (cls) => cls.class_token === value,
      );
      if (selectedClass) {
        setFormData((prev) => ({
          ...prev,
          class_token: value,
          class_name: selectedClass.class_name,
        }));
      }
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<UserFormData> = {};

    if (((roleType as UserRole) || UserRole.STUDENT) === UserRole.STUDENT) {
      Object.assign(newErrors, validateStudentInput(formData).errors);
    } else {
      // Required fields
      if (!formData.first_name.trim()) {
        newErrors.first_name = 'First name is required';
      }

      if (!formData.last_name.trim()) {
        newErrors.last_name = 'Last name is required';
      }

      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else {
        const emailError = validateEmail(formData.email);
        if (emailError) {
          newErrors.email = emailError;
        }
      }

      if (!formData.dob) {
        newErrors.dob = 'Date of birth is required';
      } else if (!validateDob(formData.dob)) {
        newErrors.dob = 'Please enter a valid date of birth';
      }

      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (
        roleType === UserRole.TEACHER &&
        formData.password !== formData.dob.replace(/[-/]/g, '')
      ) {
        const passwordError = staffPasswordError(formData.password);
        if (passwordError) newErrors.password = passwordError;
      }

      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    // Validate class selection for students (optional but if provided must be valid)
    if (
      ((roleType as UserRole) || UserRole.STUDENT) === UserRole.STUDENT &&
      formData.class_token &&
      !availableClasses.find((cls) => cls.class_token === formData.class_token)
    ) {
      newErrors.class_token = 'Please select a valid class';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (isSubmitting || !validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const currentRole = (roleType as UserRole) || UserRole.STUDENT;

      // For students, use the enhanced addNewStudent function
      if (currentRole === UserRole.STUDENT) {
        // Build UserProfileForm data for addNewStudent
        const studentData = {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          dob: formData.dob,
        };

        const result = await addNewStudent(
          studentData,
          supabase,
          currentSemester,
          formData.class_token || undefined,
          formData.class_token ? formData.class_name || undefined : undefined,
        );

        if (result && result.data && result.data.length > 0) {
          const successMessage =
            result.outcome !== 'created'
              ? 'Student enrollment saved. Existing password is unchanged.'
              : formData.class_token
              ? `Student created and assigned to class ${formData.class_name}`
              : 'Student created successfully';

          toast({
            title:
              result.outcome === 'created'
                ? 'Student Created Successfully'
                : 'Student Enrollment Saved',
            description: successMessage,
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
        } else {
          throw new Error('Failed to create student');
        }
      } else if (currentRole === UserRole.TEACHER) {
        creationRequestId.current ||= crypto.randomUUID();
        await new AccountService(supabase).createTeacher(
          {
            first_name: formData.first_name,
            last_name: formData.last_name,
            email: formData.email,
            dob: formData.dob,
            password: formData.password,
          },
          creationRequestId.current,
        );
        toast({
          title: 'Teacher Created Successfully',
          description:
            'The teacher profile and login account are ready to use.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        // Administrator provisioning retains its existing workflow.
        const userData: UserProfile = {
          first_name: formData.first_name,
          last_name: formData.last_name,
          full_name: `${formData.first_name} ${formData.last_name}`,
          email: formData.email,
          username: '',
          dob: formData.dob,
          role: currentRole,
          created_at: new Date().toISOString(),
          deleted: false,
        };
        const { error } = await userOps.addNewUser(userData);
        if (error) {
          throw error;
        }

        toast({
          title: 'User Created Successfully',
          description: `${displayRole()} has been added to the system.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }

      // Navigate to the appropriate list page
      navigate(`/admin/${currentRole.toLowerCase()}`);
    } catch (err) {
      console.error('Error creating user:', err);
      toast({
        title: 'Error',
        description:
          err instanceof Error
            ? err.message
            : 'An unexpected error occurred while creating the user.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(
      `/admin/${((roleType as UserRole) || UserRole.STUDENT).toLowerCase()}`,
    );
  };

  return (
    <FormPageLayout
      title={`Add New ${displayRole()}`}
      // subtitle={`Create a new ${displayRole().toLowerCase()} account`}
      breadcrumb={{
        items: [
          { label: 'Admin', href: '/admin' },
          {
            label: displayRole() + 's',
            href: `/admin/${(
              (roleType as UserRole) || UserRole.STUDENT
            ).toLowerCase()}`,
          },
          { label: 'Add New', isCurrent: true },
        ],
      }}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      submitText="Create User"
      maxWidth="960px"
      loading={isSubmitting}
      submitDisabled={isSubmitting}
    >
      <VStack spacing={5} align="stretch">
        <VStack
          as="section"
          spacing={3}
          align="stretch"
          aria-labelledby="new-user-personal-heading"
        >
          <HStack justify="space-between" spacing={3} flexWrap="wrap">
            <Text
              as="h2"
              id="new-user-personal-heading"
              fontSize="md"
              fontWeight="semibold"
            >
              Personal Information
            </Text>
            <Badge colorScheme="blue" borderRadius="md" px={2} py={1}>
              {displayRole()}
            </Badge>
          </HStack>
          <SimpleGrid columns={{ base: 1, md: 2 }} columnGap={5} rowGap={4}>
            <FormField label="First Name" required error={errors.first_name}>
              <Input
                value={formData.first_name}
                onChange={(e) =>
                  handleInputChange('first_name', e.target.value)
                }
                placeholder="Enter first name"
              />
            </FormField>

            <FormField label="Last Name" required error={errors.last_name}>
              <Input
                value={formData.last_name}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
                placeholder="Enter last name"
              />
            </FormField>

            <FormField
              label="Email Address"
              required
              error={errors.email}
              helperText={
                roleType === UserRole.STUDENT
                  ? 'Used for communication'
                  : 'Used for login'
              }
            >
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="Enter email address"
              />
            </FormField>

            <FormField label="Date of Birth" required error={errors.dob}>
              <Input
                type="date"
                value={formData.dob}
                onChange={(e) => handleInputChange('dob', e.target.value)}
              />
            </FormField>
          </SimpleGrid>
        </VStack>

        <VStack
          as="section"
          spacing={3}
          align="stretch"
          aria-labelledby="new-user-login-heading"
        >
          <Text
            as="h2"
            id="new-user-login-heading"
            fontSize="md"
            fontWeight="semibold"
          >
            Login Details
          </Text>
          {((roleType as UserRole) || UserRole.STUDENT) ===
            UserRole.STUDENT && (
            <FormField
              label="Username"
              helperText="Generated automatically from the first name and email, in lowercase with spaces removed."
            >
              <Input
                readOnly
                value={
                  formData.first_name.trim() && formData.email.trim()
                    ? generateStudentUsername(
                        formData.first_name,
                        formData.email,
                      )
                    : ''
                }
                placeholder="Enter first name and email to preview"
                bg="gray.50"
              />
            </FormField>
          )}
          {((roleType as UserRole) || UserRole.STUDENT) === UserRole.STUDENT ? (
            <Text fontSize="sm" color="gray.600">
              New students use their birthday (YYYYMMDD) as the initial
              password. Existing student passwords stay unchanged.
            </Text>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2 }} columnGap={5} rowGap={4}>
              <FormField
                label="Password"
                required
                error={errors.password}
                helperText={
                  roleType === UserRole.TEACHER
                    ? 'Use at least 6 characters of any kind, or the birthday as YYYYMMDD. No verification email is sent.'
                    : 'Must be at least 6 characters long'
                }
              >
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={(e) =>
                    handleInputChange('password', e.target.value)
                  }
                  placeholder="Enter password"
                />
              </FormField>

              <FormField
                label="Confirm Password"
                required
                error={errors.confirmPassword}
              >
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={formData.confirmPassword}
                  onChange={(e) =>
                    handleInputChange('confirmPassword', e.target.value)
                  }
                  placeholder="Confirm password"
                />
              </FormField>
            </SimpleGrid>
          )}
        </VStack>

        {((roleType as UserRole) || UserRole.STUDENT) === UserRole.STUDENT && (
          <VStack
            as="section"
            spacing={3}
            align="stretch"
            aria-labelledby="new-user-class-heading"
          >
            <Box>
              <Text
                as="h2"
                id="new-user-class-heading"
                fontSize="md"
                fontWeight="semibold"
              >
                Class Assignment{' '}
                <Text
                  as="span"
                  fontSize="sm"
                  fontWeight="normal"
                  color="gray.500"
                >
                  (Optional)
                </Text>
              </Text>
              <Text fontSize="sm" color="gray.500" mt={1}>
                Assign a class for {currentSemester || 'the current semester'}.
              </Text>
            </Box>
            <FormField
              label="Select Class"
              error={errors.class_token}
              helperText={
                availableClasses.length === 0
                  ? isLoadingClasses
                    ? 'Loading available classes...'
                    : 'No classes available for assignment'
                  : `${availableClasses.length} class${
                      availableClasses.length > 1 ? 'es' : ''
                    } available for assignment`
              }
            >
              <Select
                value={formData.class_token || ''}
                onChange={(e) =>
                  handleInputChange('class_token', e.target.value)
                }
                placeholder={
                  isLoadingClasses
                    ? 'Loading classes...'
                    : availableClasses.length === 0
                    ? 'No classes available'
                    : 'Select a class (optional)'
                }
                disabled={isLoadingClasses || availableClasses.length === 0}
              >
                <option value="">No class assignment</option>
                {availableClasses.map((classItem) => (
                  <option
                    key={classItem.class_token}
                    value={classItem.class_token}
                  >
                    {classItem.class_name} - {classItem.grade} Grade
                    {(classItem as any).teacher_name
                      ? ` (${(classItem as any).teacher_name})`
                      : ''}
                    {classItem.number_of_students !== undefined
                      ? ` - ${classItem.number_of_students} students`
                      : ''}
                  </option>
                ))}
              </Select>
            </FormField>
            {formData.class_token && (
              <Box borderLeftWidth="3px" borderColor="blue.400" pl={3} py={1}>
                <Text fontSize="sm" fontWeight="medium">
                  {formData.class_name}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  The student will receive this class’s existing homework
                  assignments.
                </Text>
              </Box>
            )}
          </VStack>
        )}
      </VStack>
    </FormPageLayout>
  );
};
